/**
 * EmptyState - Canonical empty state patterns per STREAM_AESTHETIC.md 6.1-6.2.
 *
 * Renders standardized empty state UI for various contexts: first-use,
 * filtered results, no results, error, partial load, and streaming paused.
 *
 * @example First-use empty state with CTA
 * ```tsx
 * <EmptyState
 *   variant="first-use"
 *   title="No customers yet"
 *   description="Start by adding your first customer to the platform."
 *   action={<Button>Add Customer</Button>}
 * />
 * ```
 *
 * @example Filtered empty state (compact)
 * ```tsx
 * <EmptyState
 *   variant="filtered"
 *   title="No results match your filters"
 *   action={<Button variant="link">Clear filters</Button>}
 *   size="sm"
 * />
 * ```
 */

import type { CSSProperties, ReactNode } from 'react'
import {
  Inbox,
  Filter,
  Search,
  AlertCircle,
  AlertTriangle,
  PauseCircle,
} from 'lucide-react'

// ── Types ────────────────────────────────────────────────────────────────────

export type EmptyVariant =
  | 'first-use'
  | 'filtered'
  | 'no-results'
  | 'error'
  | 'partial'
  | 'streaming-paused'

export interface EmptyStateProps {
  /** Empty state variant determining default icon and styling */
  variant: EmptyVariant
  /** Custom icon - overrides default variant-mapped icon */
  icon?: ReactNode
  /** Heading text (required) */
  title: string
  /** Body description */
  description?: string | ReactNode
  /** Primary CTA button or link */
  action?: ReactNode
  /** Secondary action */
  secondaryAction?: ReactNode
  /** Size variant: 'sm' uses compact padding, 'md' is default */
  size?: 'sm' | 'md'
}

// ── Default Icons ────────────────────────────────────────────────────────────

const variantToIcon: Record<EmptyVariant, ReactNode> = {
  'first-use': <Inbox />,
  'filtered': <Filter />,
  'no-results': <Search />,
  'error': <AlertCircle />,
  'partial': <AlertTriangle />,
  'streaming-paused': <PauseCircle />,
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * EmptyState renders a centered, accessible empty state message.
 *
 * Features:
 * - Six variants covering all common empty scenarios
 * - Default icons per variant, overridable
 * - Two size variants for different contexts
 * - Slots for primary and secondary actions
 * - Accessible heading structure
 */
export function EmptyState({
  variant,
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'md',
}: EmptyStateProps) {
  const displayIcon = icon !== undefined ? icon : variantToIcon[variant]
  const isCompact = size === 'sm'

  const containerStyle: CSSProperties = {
    ...styles.container,
    padding: isCompact
      ? `var(--empty-state-padding-y-sm) 16px`
      : `var(--empty-state-padding-y) 24px`,
  }

  const iconSize = isCompact ? 24 : 32

  return (
    <div style={containerStyle} role="status" aria-live="polite">
      {displayIcon && (
        <div
          style={{
            ...styles.iconWrapper,
            width: iconSize,
            height: iconSize,
          }}
        >
          {/* Clone icon with appropriate size if it's a Lucide icon */}
          {typeof displayIcon === 'object' && 'type' in displayIcon
            ? { ...displayIcon, props: { ...displayIcon.props, size: iconSize } }
            : displayIcon}
        </div>
      )}

      <h3 style={isCompact ? styles.titleSm : styles.title}>{title}</h3>

      {description && (
        <p style={isCompact ? styles.descriptionSm : styles.description}>
          {description}
        </p>
      )}

      {(action || secondaryAction) && (
        <div style={styles.actions}>
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 'var(--empty-state-gap)',
    minHeight: '200px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--empty-state-icon-color)',
    marginBottom: '8px',
  },
  title: {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--empty-state-title-color)',
    margin: 0,
    lineHeight: 1.4,
  },
  titleSm: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--empty-state-title-color)',
    margin: 0,
    lineHeight: 1.4,
  },
  description: {
    fontSize: '13px',
    fontWeight: 400,
    color: 'var(--empty-state-body-color)',
    margin: 0,
    lineHeight: 1.5,
    maxWidth: '360px',
  },
  descriptionSm: {
    fontSize: '12px',
    fontWeight: 400,
    color: 'var(--empty-state-body-color)',
    margin: 0,
    lineHeight: 1.5,
    maxWidth: '300px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '8px',
  },
}

export default EmptyState
